import {
  getComparisonResearchQueriesListUrl,
  getComparisonResearchQueryConfigUrl,
  getComparisonResearchQueryBackupUrl,
  getComparisonResearchQueryByIdUrl,
  validateImportedComparisonResearchQueryUrl,
  getAllComparisonResearchQueriesUrl,
  assignResearchQueryToRuleUrl,
  reactivateComparisonResearchQueryUrl,
  cloneComparisonResearchQueryUrl,
} from "./constants";
import base from "../middlewares/interceptors";

export const getComparisonResearchQueriesList = async () => {
  try {
    const response = await base.get(getComparisonResearchQueriesListUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedComparisonResearchQueries = async ({
  page,
  pSize,
  ruleId,
  comparisonResearchQueryId,
  statusFilter,
  signal,
}: any) => {
  try {
    const defaultPage = process.env.REACT_APP_DEFAULT_PAGE || "1";
    const defaultPageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE || "25";

    const pageN = page || defaultPage;
    const pageSize = pSize || defaultPageSize;

    const params: { [key: string]: any } = {
      page: pageN,
      size: pageSize,
    };

    // Add rule_id filter parameter if ruleId is provided
    // Supports both single value (string/number) and multiple values (array)
    if (ruleId) {
      if (Array.isArray(ruleId)) {
        // Handle multiple rule IDs - join with comma for API
        if (ruleId.length > 0) {
          params.rule_filter_ids = ruleId.join(",");
        }
      } else if (String(ruleId).trim()) {
        // Handle single rule ID
        params.rule_filter_ids = String(ruleId).trim();
      }
    }
    // Add comparison_research_query_id filter parameter if comparisonResearchQueryId is provided
    // Supports both single value (string/number) and multiple values (array)
    if (comparisonResearchQueryId) {
      if (Array.isArray(comparisonResearchQueryId)) {
        // Handle multiple comparison research query IDs - join with comma for API
        if (comparisonResearchQueryId.length > 0) {
          params.comparison_research_queries_filter_ids =
            comparisonResearchQueryId.join(",");
        }
      } else if (String(comparisonResearchQueryId).trim()) {
        // Handle single comparison research query ID
        params.comparison_research_queries_filter_ids = String(
          comparisonResearchQueryId,
        ).trim();
      }
    }

    // Add status filter parameter if statusFilter is provided
    // Convert status filter to is_active boolean parameter:
    // - "Active" -> is_active=true
    // - "Inactive" -> is_active=false
    // - "All" or empty -> no is_active parameter (returns all)
    if (statusFilter && statusFilter.trim()) {
      const trimmedStatus = statusFilter.trim();
      if (trimmedStatus === "Active") {
        params.is_active = true;
      } else if (trimmedStatus === "Inactive") {
        params.is_active = false;
      }
      // For "All" or any other value, don't add is_active parameter
    }

    const response = await base.get(getComparisonResearchQueryConfigUrl, {
      params,
      signal, // Add abort signal for request cancellation
    });
    return response.data;
  } catch (error) {
    console.error(error);
    // Handle error as needed, you might want to throw it or log it to a monitoring service
    throw error;
  }
};

export const getComparisonResearchQueryBackup = async ({
  comparisonResearchQueryId,
  page,
  pSize,
}: any) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    let requestUrl;
    if (comparisonResearchQueryId && comparisonResearchQueryId !== 0) {
      requestUrl = `${getComparisonResearchQueryBackupUrl}?comparison_research_queries_backup_filter_ids=${comparisonResearchQueryId}&page=${pageN}&size=${pageS}`;
    }
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getResearchQueryByIdDetail = async (comparisonId: number) => {
  try {
    const response = await base.get(
      `${getComparisonResearchQueryByIdUrl}/${comparisonId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const ValidateImportedComparisonResearchQuery = async (reqBody: any) => {
  try {
    const response = await base.post(
      `${validateImportedComparisonResearchQueryUrl}/${reqBody?.entity_description?.id}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
/**
 * Fetch all comparison research queries (non-paginated) for dropdown selection
 */
export const getAllComparisonResearchQueries = async () => {
  try {
    const response = await base.get(getAllComparisonResearchQueriesUrl);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
/**
 * Assign or unassign a research query to/from a rule
 * @param payload - Contains comparison_research_query_id (path), assign (query), and rule_id (query)
 * - comparison_research_query_id: The ID of the research query (required)
 * - assign: True to assign, False to unassign (required)
 * - rule_id: The rule ID to assign to (number when assign=true, null when unassigning)
 */
export const assignResearchQueryToRule = async (payload: {
  comparison_research_query_id: number;
  assign: boolean;
  rule_id: number | null;
}) => {
  try {
    const { comparison_research_query_id, assign, rule_id } = payload;

    // Build query params - always include both assign and rule_id
    const params: { assign: boolean; rule_id: number | null } = {
      assign,
      rule_id,
    };

    const response = await base.put(
      `${assignResearchQueryToRuleUrl}/${comparison_research_query_id}`,
      null, // no body needed
      { params },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
export const deleteComparisonResearchQuery = async (
  comparisonResearchQueryId: number,
) => {
  try {
    const response = await base.delete(
      `${getComparisonResearchQueryByIdUrl}/${comparisonResearchQueryId}`,
    );
    return { response: response.data, status: response.status };
  } catch (error) {
    console.error(error);
  }
};
/**
 * Reactivate comparison research query
 * @param comparisonResearchQueryId - The ID of the comparison research query to reactivate
 * @returns Promise with reactivation result
 */
export const reactivateComparisonResearchQuery = async (
  comparisonResearchQueryId: string | number,
) => {
  try {
    const response = await base.put(
      `${reactivateComparisonResearchQueryUrl}/${comparisonResearchQueryId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error("Error reactivating comparison research query:", error);
    throw error;
  }
};

/**
 * Clone comparison research query
 * @param comparisonResearchQueryId - ID of the comparison research query to clone
 * @param ruleId - ID of the rule to clone to
 * @returns Promise with cloned comparison research query data
 */
export const cloneComparisonResearchQuery = async (
  comparisonResearchQueryId: number,
  ruleId: number,
) => {
  try {
    const response = await base.post(
      `${cloneComparisonResearchQueryUrl}/${comparisonResearchQueryId}?rule_id=${ruleId}`,
      {}, // Empty body as per API spec
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
